/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-12
 */

// Add third-party dependencies.
import { Card, CardContent, Typography, Box } from '@mui/material';
import { Business, Group, Mail, TrendingUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import type { DashboardOverviewResponse } from '../../../../types/dashboard';
import { ARIA_ROLES } from '../../../../utils/accessibility';
import { calculateStudioOwnershipMetrics } from '../../../../utils/dashboardHelpers';

interface StatsCardProps {
  dashboardData?: DashboardOverviewResponse;
}

/**
 * Statistics card component for the dashboard.
 * Displays studio ownership metrics and real data from API.
 *
 * @example
 * return (
 *   <StatsCard dashboardData={dashboardData} />
 * );
 *
 * @returns {React.ReactElement} The statistics card component
 */
const StatsCard: React.FC<StatsCardProps> = ({ dashboardData }) => {
  const { t } = useTranslation('dashboard');

  // Extract data from API response
  const apiStats = dashboardData?.data?.stats;
  const studios = dashboardData?.data?.studios || [];
  const userSummary = dashboardData?.data?.user_summary;

  // Calculate studio ownership metrics
  const ownershipMetrics = calculateStudioOwnershipMetrics(studios);

  const stats = [
    {
      label: t('stats.labels.studiosOwned'),
      value: ownershipMetrics.ownedStudios.toString(),
      icon: <Business sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: t('stats.descriptions.studiosOwned'),
    },
    {
      label: t('stats.labels.totalStudios'),
      value: (apiStats?.total_studios ?? studios.length).toString(),
      icon: <Group sx={{ fontSize: 40, color: 'secondary.main' }} />,
      description: t('stats.descriptions.totalStudios'),
    },
    {
      label: t('stats.labels.pendingInvitations'),
      value: (apiStats?.pending_invitations ?? 0).toString(),
      icon: <Mail sx={{ fontSize: 40, color: 'warning.main' }} />,
      description: t('stats.descriptions.pendingInvitations'),
    },
    {
      label: t('stats.labels.profileCompletion'),
      value: userSummary?.profile_completion
        ? `${userSummary.profile_completion}%`
        : '0%',
      icon: <TrendingUp sx={{ fontSize: 40, color: 'info.main' }} />,
      description: t('stats.descriptions.profileCompletion'),
    },
  ];

  return (
    <Card role={ARIA_ROLES.COMPLEMENTARY} aria-label={t('aria.statisticsCard')}>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('stats.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('stats.description')}
        </Typography>

        <Box
          role="group"
          aria-label={t('aria.statisticsOverview')}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {stats.map(stat => (
            <Box
              key={stat.label}
              role="img"
              aria-label={t('aria.statValue', {
                label: stat.label,
                value: stat.value,
                description: stat.description,
              })}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                height: '100%',
              }}
            >
              <Box aria-hidden="true">{stat.icon}</Box>
              <Typography
                variant="h4"
                component="div"
                sx={{ mt: 1, mb: 0.5 }}
                aria-hidden="true"
              >
                {stat.value}
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.primary"
                gutterBottom
                aria-hidden="true"
              >
                {stat.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                aria-hidden="true"
              >
                {stat.description}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
          role="note"
          aria-label={t('aria.futureFeatures')}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            {t('stats.comingSoon')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatsCard;
